import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { getCSVConfig, isValidCSVFile, CSV_FILES_INFO } from '../../config/csv-mappings'
import type { ETLProgress } from '../../types/etl.types'
import MunicipiosJSONUploader from './MunicipiosJSONUploader'

export default function ETLUploader() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [progress, setProgress] = useState<ETLProgress>({
    stage: 'idle',
    percentage: 0,
    message: '',
  })
  const [useEdgeFunction, setUseEdgeFunction] = useState(true)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!isValidCSVFile(file.name)) {
      alert(`Archivo no reconocido: ${file.name}\n\nArchivos válidos:\n${CSV_FILES_INFO.map(f => f.name).join('\n')}`)
      return
    }

    setSelectedFile(file)
    setProgress({ stage: 'idle', percentage: 0, message: '' })
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    try {
      setProgress({ stage: 'uploading', percentage: 10, message: 'Subiendo archivo...' })

      // 1. Subir a Supabase Storage
      const fileName = `${Date.now()}_${selectedFile.name}`
      const { error: uploadError } = await supabase.storage
        .from('csv-uploads')
        .upload(fileName, selectedFile)

      if (uploadError) {
        throw new Error(`Error al subir: ${uploadError.message}`)
      }

      setProgress({ stage: 'processing', percentage: 30, message: 'Procesando datos...' })

      // 2. Obtener configuración del archivo
      const config = getCSVConfig(selectedFile.name)
      if (!config) {
        throw new Error('Configuración no encontrada para este archivo')
      }

      // 3. Intentar Edge Function primero
      if (useEdgeFunction) {
        try {
          const { data, error } = await supabase.functions.invoke('process-csv', {
            body: {
              fileName,
              targetTable: config.targetTable,
              mappingConfig: config.mapping,
            },
          })

          if (error) throw error

          setProgress({
            stage: 'complete',
            percentage: 100,
            message: `✅ ${data.insertedRows || 0} registros insertados en ${config.targetTable}`,
            totalRows: data.totalRows,
            processedRows: data.insertedRows,
          })

          // Limpiar después de 3 segundos
          setTimeout(() => {
            setSelectedFile(null)
            setProgress({ stage: 'idle', percentage: 0, message: '' })
          }, 3000)
        } catch (edgeFunctionError) {
          console.warn('Edge Function falló, usando fallback:', edgeFunctionError)
          setProgress({
            stage: 'error',
            percentage: 0,
            message: `⚠️ Edge Function no disponible. Por favor, usa el fallback Web Worker.`,
          })
        }
      } else {
        // TODO: Implementar Web Worker fallback
        setProgress({
          stage: 'error',
          percentage: 0,
          message: '⚠️ Web Worker aún no implementado. Usa Edge Function.',
        })
      }
    } catch (error) {
      console.error('Error en ETL:', error)
      setProgress({
        stage: 'error',
        percentage: 0,
        message: `❌ Error: ${error instanceof Error ? error.message : 'Error desconocido'}`,
      })
    }
  }

  const fileInfo = selectedFile
    ? CSV_FILES_INFO.find((f) => f.name === selectedFile.name)
    : null

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Cargador de Municipios JSON */}
      <MunicipiosJSONUploader />

      {/* Card Principal CSV */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-forest-600 to-forest-700">
          <h2 className="text-2xl font-bold text-white mb-2">Carga de Datos CSV</h2>
          <p className="text-forest-100">
            Procesa archivos CSV y cárgalos en PostgreSQL
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Selector de archivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar archivo CSV
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              disabled={progress.stage !== 'idle' && progress.stage !== 'error'}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-forest-50 file:text-forest-700 hover:file:bg-forest-100 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Información del archivo */}
          {selectedFile && fileInfo && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 text-2xl">📄</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-600 mt-1">{fileInfo.description}</p>
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    <span>
                      <strong>Tamaño:</strong> {(selectedFile.size / 1024).toFixed(2)} KB
                    </span>
                    <span>
                      <strong>Registros esperados:</strong>{' '}
                      {fileInfo.records.toLocaleString('es-ES')}
                    </span>
                    <span>
                      <strong>Tabla destino:</strong> {getCSVConfig(selectedFile.name)?.targetTable}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Opción de método */}
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="use-edge-function"
              checked={useEdgeFunction}
              onChange={(e) => setUseEdgeFunction(e.target.checked)}
              disabled={progress.stage !== 'idle' && progress.stage !== 'error'}
              className="w-4 h-4 text-forest-600 rounded focus:ring-forest-500"
            />
            <label htmlFor="use-edge-function" className="text-sm text-gray-700">
              Usar Supabase Edge Function (recomendado - procesamiento serverless)
            </label>
          </div>

          {/* Botón de carga */}
          <button
            onClick={handleUpload}
            disabled={!selectedFile || (progress.stage !== 'idle' && progress.stage !== 'error')}
            className="w-full bg-forest-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-forest-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {progress.stage === 'idle' && '📤 Subir y Procesar CSV'}
            {progress.stage === 'uploading' && '⏳ Subiendo...'}
            {progress.stage === 'processing' && '⚙️ Procesando...'}
            {progress.stage === 'complete' && '✅ Completado'}
            {progress.stage === 'error' && '🔄 Reintentar'}
          </button>

          {/* Barra de progreso */}
          {progress.stage !== 'idle' && (
            <div className="space-y-2">
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    progress.stage === 'complete'
                      ? 'bg-green-600'
                      : progress.stage === 'error'
                      ? 'bg-red-600'
                      : 'bg-forest-600'
                  }`}
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
              <p
                className={`text-sm text-center font-medium ${
                  progress.stage === 'complete'
                    ? 'text-green-700'
                    : progress.stage === 'error'
                    ? 'text-red-700'
                    : 'text-gray-700'
                }`}
              >
                {progress.message}
              </p>
              {progress.processedRows && progress.totalRows && (
                <p className="text-xs text-center text-gray-500">
                  {progress.processedRows.toLocaleString('es-ES')} de{' '}
                  {progress.totalRows.toLocaleString('es-ES')} registros procesados
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabla de información de archivos */}
      <div className="mt-8 bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 bg-gray-50 border-b">
          <h3 className="font-semibold text-gray-900">Archivos CSV Disponibles</h3>
          <p className="text-sm text-gray-600 mt-1">
            Total: {CSV_FILES_INFO.reduce((sum, f) => sum + f.records, 0).toLocaleString('es-ES')}{' '}
            registros en 13 archivos
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">#</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Archivo</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Descripción</th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">Registros</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {CSV_FILES_INFO.map((file, index) => (
                <tr key={file.name} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-900">{file.name}</td>
                  <td className="px-4 py-3 text-gray-600">{file.description}</td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    {file.records.toLocaleString('es-ES')}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td colSpan={3} className="px-4 py-3 text-right text-gray-700">
                  Total:
                </td>
                <td className="px-4 py-3 text-right text-gray-900">
                  {CSV_FILES_INFO.reduce((sum, f) => sum + f.records, 0).toLocaleString('es-ES')}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Instrucciones */}
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          <strong>📌 Nota importante:</strong> Antes de cargar los CSV, asegúrate de haber ejecutado
          el esquema SQL en Supabase Dashboard. Los archivos deben cargarse en orden para respetar
          las relaciones de claves foráneas.
        </p>
      </div>
    </div>
  )
}
